import { useQueryClient } from "@tanstack/react-query";
import React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { type PaginatedResponse } from "@/shared/http";

import { eventQueryKeys } from "./eventQueryKeys";

/** Page size requested per search. Bounded so the caller renders a short list
 *  and the user types to narrow rather than scrolling. */
const SEARCH_LIMIT = 20;

/**
 * Lazy, server-side search over an org's event tags.
 *
 * Returns a stable function that resolves one page of matching tags. Unlike
 * {@link useEventTags}, nothing is fetched until it is called.
 */
export function useEventTagSearch(
  orgId: string,
): (
  search: string,
  signal?: AbortSignal,
) => Promise<PaginatedResponse<string>> {
  const { events } = useDomainServices();
  const queryClient = useQueryClient();

  return React.useCallback(
    (search, signal) => {
      const fetchPage = (abortSignal?: AbortSignal) =>
        events.getTagsForOrg(
          { search: search || undefined, limit: SEARCH_LIMIT },
          { resourceOwnerId: orgId, abortSignal },
        );

      if (search !== "") {
        return fetchPage(signal);
      }

      return queryClient.ensureQueryData({
        queryKey: eventQueryKeys.orgTagSearch(orgId, ""),
        queryFn: ({ signal: querySignal }) => fetchPage(querySignal),
      });
    },
    [queryClient, events, orgId],
  );
}
